import React, { useState, useRef, useEffect } from 'react';
import { fetchUnifiedData } from './dataFetcher';
import { buildChartData } from './chartConfigBuilder';
import CombinedChart from './CombinedChart';
import SelectionPanel from './SelectionPanel';
import { economicOptions, intervalOptionsMap, maturityOptions, commodityOptions } from './dataOptions';
import styles from './VisualizationStyles.module.css';
import axiosInstance from '../../../axiosConfig';
import { SummaryDisplay3 } from './SummaryDisplay3';

const formatDateForInput = (date) => date.toISOString().split('T')[0];

const getDefaultDateRangeForDatasets = (datasets) => {
  const datasetRanges = datasets
    .map((dataset) => {
      const validDates = (dataset.data || [])
        .map((point) => new Date(point.x))
        .filter((date) => !Number.isNaN(date.getTime()));

      if (validDates.length === 0) {
        return null;
      }

      const timestamps = validDates.map((date) => date.getTime());
      return {
        min: new Date(Math.min(...timestamps)),
        max: new Date(Math.max(...timestamps)),
        spanMs: Math.max(...timestamps) - Math.min(...timestamps)
      };
    })
    .filter(Boolean);

  if (datasetRanges.length === 0) {
    return null;
  }

  const overlapStart = new Date(Math.max(...datasetRanges.map((range) => range.min.getTime())));
  const overlapEnd = new Date(Math.min(...datasetRanges.map((range) => range.max.getTime())));

  if (overlapStart <= overlapEnd) {
    return {
      startDate: formatDateForInput(overlapStart),
      endDate: formatDateForInput(overlapEnd)
    };
  }

  const shortestRange = datasetRanges.reduce((shortest, current) => {
    return current.spanMs < shortest.spanMs ? current : shortest;
  });

  return {
    startDate: formatDateForInput(shortestRange.min),
    endDate: formatDateForInput(shortestRange.max)
  };
};

const CombinedViewer = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [rawDatasets, setRawDatasets] = useState([]);
  const [filteredDatasets, setFilteredDatasets] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dataErrors, setDataErrors] = useState([]); // Add state for tracking errors
  const [sessionData, setSessionData] = useState(null); // Add state for session analysis
  const [isAnalyzing, setIsAnalyzing] = useState(false); // Add state for analysis loading
  const [chartRef, setChartRef] = useState(null); // Add state for chart reference
  const [analysisHistory, setAnalysisHistory] = useState([]); // Store analysis history
  const [currentAnalysisIndex, setCurrentAnalysisIndex] = useState(-1); // Current analysis index
  const maturityRef = useRef();
  const intervalRef = useRef();

  const unifiedOptions = [
    ...economicOptions.map(opt => ({ ...opt, type: 'economic' })),
    ...commodityOptions.map(opt => ({ ...opt, type: 'commodity' }))
  ];

  const handleSelectItem = (code) => {
    const found = unifiedOptions.find(opt => opt.code === code);
    if (!found) return;

    if (found.type === 'economic' && ['REAL_GDP', 'TREASURY_YIELD', 'FEDERAL_FUNDS_RATE'].includes(found.code)) {
      // Clear any existing pending selection when selecting a new economic variable
      setPendingSelection({ ...found, economy: found.code });
    } else {
      // For non-economic items or economic items that don't need additional selections, 
      // clear pending selection and add directly to selected items
      setPendingSelection(null);
      setSelectedItems(prev => [...prev, found]);
    }
  };

  const handleSelectCompany = (code, companyData) => {
    setSelectedItems(prev => [...prev, companyData]);
  };

  const handleSelectFx = (code, fxData) => {
    setSelectedItems(prev => [...prev, fxData]);
  };

  const handleSelectCrypto = (code, cryptoData) => {
    setSelectedItems(prev => [...prev, cryptoData]);
  };

  const handleIntervalSelect = (interval) => {
    if (!pendingSelection) return;
    const updated = { ...pendingSelection, interval };

    if (updated.economy !== 'TREASURY_YIELD' || updated.maturity) {
      setSelectedItems(prev => [...prev, updated]);
      setPendingSelection(null);
    } else {
      setPendingSelection(updated);
    }
  };

  const handleMaturitySelect = (maturity) => {
    if (pendingSelection) {
      setPendingSelection(prev => ({ ...prev, maturity }));
    }
  };

  const handleDeleteItem = (index) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleFetchAndPlot = async () => {
    setIsLoading(true);
    setDataErrors([]); // Clear previous errors
    
    // Clear analysis when plotting new chart
    setSessionData(null);
    
    try {
      const { econData, comData, companyData, fxData, cryptoData } = await fetchUnifiedData(selectedItems);
      const fullDatasets = buildChartData(econData, comData, companyData, fxData, cryptoData, selectedItems);

      const defaultDateRange = getDefaultDateRangeForDatasets(fullDatasets);
      if (defaultDateRange) {
        setStartDate(defaultDateRange.startDate);
        setEndDate(defaultDateRange.endDate);
      } else {
        setStartDate('');
        setEndDate('');
      }

      setRawDatasets(fullDatasets);
      
      // Check which selected items actually made it into the final datasets
      const plottedItemNames = fullDatasets.map(dataset => dataset.label);
      //console.log('DEBUG: Plotted chart labels:', plottedItemNames);
      //console.log('DEBUG: Selected items:', selectedItems);
      
      const failedItems = selectedItems.filter(item => {
        // Different data types have different label patterns in the chart
        let isPlotted = false;
        
        if (item.type === 'economic') {
          // Economics data uses codes with intervals/maturity appended
          // Backend format: {code}_{interval}_{maturity}
          let expectedLabel = item.code;
          if (item.interval) {
            expectedLabel += `_${item.interval}`;
          }
          if (item.maturity) {
            expectedLabel += `_${item.maturity}`;
          }
          //console.log(`DEBUG: Looking for economics label "${expectedLabel}" in:`, plottedItemNames);
          isPlotted = plottedItemNames.includes(expectedLabel);
        } else if (item.type === 'company') {
          // Company data uses "DisplayName (Stock Price)" format
          const expectedLabel = `${item.name} (Stock Price)`;
          isPlotted = plottedItemNames.includes(expectedLabel);
        } else if (item.type === 'fx') {
          // FX data uses "DisplayName (Exchange Rate)" format
          const expectedLabel = `${item.name} (Exchange Rate)`;
          isPlotted = plottedItemNames.includes(expectedLabel);
        } else if (item.type === 'crypto') {
          // Crypto data uses "DisplayName (Crypto Price)" format
          const expectedLabel = `${item.name} (Crypto Price)`;
          isPlotted = plottedItemNames.includes(expectedLabel);
        } else if (item.type === 'commodity') {
          // Commodity data: check if any plotted label contains the commodity code
          // Since commodity labels come from API response and might vary
          isPlotted = plottedItemNames.some(label => 
            label.includes(item.code) || label.includes(item.name)
          );
        }
        
        return !isPlotted;
      });
      
      // Only show errors for items that truly didn't make it into the final chart
      if (failedItems.length > 0) {
        const finalErrors = failedItems.map(item => {
          if (item.type === 'economic') {
            return `Economic indicator '${item.code}' is not currently available. Please try a different indicator.`;
          } else if (item.type === 'commodity') {
            return `Commodity '${item.code}' is not currently available. Please try a different commodity.`;
          } else if (item.type === 'company') {
            return `Company data for '${item.code}' is not currently available. Please try a different company.`;
          } else if (item.type === 'fx') {
            return `Foreign exchange pair '${item.code}' is not currently available. Please try a different pair.`;
          } else if (item.type === 'crypto') {
            return `Crypto pair '${item.code}' is not currently available. Please try a different pair.`;
          }
          return `Data for '${item.name}' is not currently available.`;
        });
        setDataErrors(finalErrors);
      } else if (fullDatasets.length === 0 && selectedItems.length > 0) {
        // If no datasets were created but we had selected items, show a generic error
        setDataErrors(['No data could be retrieved for the selected items. Please try different selections or check back later.']);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setDataErrors(['Failed to fetch data. Please check your internet connection and try again.']);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to filter data based on date range
  const filterDataByDateRange = (datasets, startDate, endDate) => {
    return datasets.map(ds => ({
      ...ds,
      data: ds.data.filter(pt => {
        const d = new Date(pt.x);
        return (!startDate || d >= new Date(startDate)) && (!endDate || d <= new Date(endDate));
      })
    }));
  };

  // Navigation functions for analysis history
  const navigateToPreviousAnalysis = () => {
    if (currentAnalysisIndex > 0) {
      const newIndex = currentAnalysisIndex - 1;
      setCurrentAnalysisIndex(newIndex);
      setSessionData(analysisHistory[newIndex]); // Use the full object
    }
  };

  const navigateToNextAnalysis = () => {
    if (currentAnalysisIndex < analysisHistory.length - 1) {
      const newIndex = currentAnalysisIndex + 1;
      setCurrentAnalysisIndex(newIndex);
      setSessionData(analysisHistory[newIndex]); // Use the full object
    }
  };

  // Function to get data overview/analysis
  const handleGetDataOverview = async () => {
    if (filteredDatasets.length === 0) {
      alert('No data to analyze. Please plot data first.');
      return;
    }

    if (!chartRef) {
      alert('Chart not ready. Please wait a moment and try again.');
      return;
    }

    setIsAnalyzing(true);
    setSessionData(null);

    try {
      // Capture chart as base64 image
      const chartImageBase64 = chartRef.toBase64Image('image/png', 1.0);
      
      // Prepare data for backend analysis
      const analysisData = {
        chartImage: chartImageBase64,
        selectedItems: selectedItems,
        dateRange: {
          startDate: startDate,
          endDate: endDate
        }
      };

      const response = await axiosInstance.post('/api/analyze-data/', analysisData);
      
      // Create analysis entry with timestamp and chart data info
      const analysisEntry = {
        analysis: response.data.analysis,
        originalChart: response.data.originalChart, // Include the chart from backend
        timestamp: new Date().toLocaleString(),
        chartInfo: {
          datasets: selectedItems.length,
          dateRange: startDate && endDate ? `${startDate} to ${endDate}` : 'All available data',
          items: selectedItems.map(item => item.name || item.code).join(', ')
        }
      };
      
      // Add to history and set as current
      setAnalysisHistory(prev => [...prev, analysisEntry]);
      setCurrentAnalysisIndex(prev => prev + 1);
      setSessionData(analysisEntry);
    } catch (error) {
      console.error('Error analyzing data:', error);
      alert('Failed to analyze data. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Function to handle chart reference when chart is ready
  const handleChartReady = (chart) => {
    setChartRef(chart);
  };

  // Effect to automatically filter data when dates or raw data change
  useEffect(() => {
    if (rawDatasets.length > 0) {
      const filtered = filterDataByDateRange(rawDatasets, startDate, endDate);
      setFilteredDatasets(filtered);
    }
  }, [rawDatasets, startDate, endDate]);

  useEffect(() => {
    if (pendingSelection?.economy === 'TREASURY_YIELD' && !pendingSelection.maturity) {
      maturityRef.current?.focus();
    } else if (pendingSelection?.economy) {
      intervalRef.current?.focus();
    }
  }, [pendingSelection]);

  return (
    <div className={styles.combined_viewer_container}>
      <div className={styles.selection_panel_container}>
        <SelectionPanel
          unifiedOptions={unifiedOptions}
          selectedItems={selectedItems}
          pendingSelection={pendingSelection}
          onSelectItem={handleSelectItem}
          onSelectCompany={handleSelectCompany}
          onSelectFx={handleSelectFx}
          onSelectCrypto={handleSelectCrypto}
          onDeleteItem={handleDeleteItem}
          onIntervalSelect={handleIntervalSelect}
          onMaturitySelect={handleMaturitySelect}
          intervalOptionsMap={intervalOptionsMap}
          maturityOptions={maturityOptions}
          maturityRef={maturityRef}
          intervalRef={intervalRef}
        />
      </div>

      <div className={styles.date_inputs_container}>
        <label className={styles.date_label}>
          <span className={styles.date_label_text}>From:</span>
          <input 
            type="date" 
            value={startDate} 
            onChange={e => setStartDate(e.target.value)}
            style={{
              marginLeft: '8px',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '140px'
            }}
          />
        </label>
        <label className={styles.date_label}>
          <span className={styles.date_label_text}>To:</span>
          <input 
            type="date" 
            value={endDate} 
            onChange={e => setEndDate(e.target.value)}
            style={{
              marginLeft: '8px',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '140px'
            }}
          />
        </label>
      </div>

      <button 
        onClick={handleFetchAndPlot} 
        disabled={isLoading} 
        className={styles.button_plot_data}
      >
        {isLoading ? 'Loading...' : 'Plot Data'}
      </button>

      {isLoading && (
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <div>Fetching data...</div>
        </div>
      )}

      {dataErrors.length > 0 && !isLoading && (
        <div style={{ marginTop: 35, marginBottom: 20 }}>
          {dataErrors.map((error, index) => (
            <div key={index} style={{ marginBottom: 5, fontSize: '14px', color: '#666' }}>
              • {error}
            </div>
          ))}
        </div>
      )}

      {filteredDatasets.length > 0 && !isLoading && (
        <>
          <CombinedChart 
            datasets={filteredDatasets} 
            onChartReady={handleChartReady}
          />
          
          {/* Session Overview Button */}
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button 
              onClick={handleGetDataOverview}
              disabled={isAnalyzing || filteredDatasets.length === 0}
              className={styles.button_plot_data}
              style={{ 
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                opacity: isAnalyzing ? 0.7 : 1
              }}
            >
              {isAnalyzing ? 'Analyzing...' : 'Get Data Analytics'}
            </button>
          </div>

          {/* Session Analysis Results */}
          <SummaryDisplay3 
            sessionData={sessionData}
            analysisHistory={analysisHistory}
            currentAnalysisIndex={currentAnalysisIndex}
            onNavigatePrev={navigateToPreviousAnalysis}
            onNavigateNext={navigateToNextAnalysis}
          />
        </>
      )}
    </div>
  );
};

export default CombinedViewer;
